// src/Components/Modules/HR/LeaveApply.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  MenuItem,
  Alert,
  Snackbar,
  Skeleton,
  Stack,
  Divider,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Radio,
  FormControlLabel,
} from "@mui/material";
import {
  FaCalendar,
  FaPaperPlane,
  FaClipboardList,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimesCircle,
  FaSync,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const FormCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
  overflow: "hidden",
}));

const BalanceCard = styled(Card)(({ theme, status }) => {
  const colors = {
    good: { bg: "#e8f5e9", color: "#2e7d32", border: "#4caf50" },
    warning: { bg: "#fff3e0", color: "#ef6c00", border: "#ff9800" },
    danger: { bg: "#ffebee", color: "#c62828", border: "#f44336" },
  };
  const palette = colors[status] || colors.good;
  return {
    background: palette.bg,
    borderRadius: theme.spacing(2),
    border: `2px solid ${palette.border}`,
    padding: theme.spacing(2),
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
    },
  };
});

const SubmitButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(2, 4),
  fontSize: "1.1rem",
  fontWeight: 700,
  borderRadius: theme.spacing(2),
  textTransform: "none",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "&:hover": {
    background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
  },
}));

const LeaveApply = () => {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    duration_type: "full_day",
    reason: "",
  });
  const [balances, setBalances] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [myLeaves, setMyLeaves] = useState([]);

  useEffect(() => {
    fetchLeaveData();
  }, []);

  const fetchLeaveData = async () => {
    try {
      setLoadingData(true);
      const balRes = await AxiosAuthInstance.get("hr/leave/balance");
      if (balRes.data.status) {
        const balMap = {};
        const allowedTypes = [];

        (balRes.data.balances || []).forEach((b) => {
          balMap[b.leave_type_id] = {
            allocated: b.allocated || 0,
            used: b.used || 0,
            remaining: b.remaining || 0,
            carry_forward: b.carry_forward || 0,
            name: b.name
          };

          allowedTypes.push({
            value: b.leave_type_id,
            label: b.name,
          });
        });

        setBalances(balMap);
        setTypes(allowedTypes);
      }

      const leavesRes = await AxiosAuthInstance.get("hr/leave/my-leaves");
      setMyLeaves(Array.isArray(leavesRes.data) ? leavesRes.data : []);

    } catch (err) {
      console.error("Failed to fetch leave data", err);
      showSnackbar("Failed to load leave data", "error");
    } finally {
      setLoadingData(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.leave_type_id) {
      newErrors.leave_type_id = "Please select a leave type";
    }
    if (!form.start_date) {
      newErrors.start_date = "Start date is required";
    }
    if (!form.end_date) {
      newErrors.end_date = "End date is required";
    }
    if (form.start_date && form.end_date && form.start_date > form.end_date) {
      newErrors.end_date = "End date cannot be before start date";
    }
    if (form.duration_type === "half_day" && form.start_date !== form.end_date) {
      newErrors.end_date = "For half-day leave, start and end date must be the same.";
    }
    if (!form.reason || form.reason.trim().length < 10) {
      newErrors.reason = "Please provide a reason (minimum 10 characters)";
    }

    if (form.leave_type_id && balances[form.leave_type_id]) {
      const balance = balances[form.leave_type_id];
      const daysNeeded = form.duration_type === "half_day" ? 0.5 : calculateDays();
      if (daysNeeded > balance.remaining) {
        newErrors.start_date = `Insufficient balance. You only have ${balance.remaining} days remaining.`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async () => {
    if (!validateForm()) {
      showSnackbar("Please fix the errors in the form", "error");
      return;
    }

    const selectedType = types.find((t) => t.value === form.leave_type_id);
    const result = await Swal.fire({
      title: "Confirm Leave Application",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Leave Type:</strong> ${selectedType?.label || "N/A"}</p>
          <p><strong>From:</strong> ${form.start_date}</p>
          <p><strong>To:</strong> ${form.end_date}</p>
          <p><strong>Duration:</strong> ${form.duration_type === 'half_day' ? 'Half Day (0.5)' : `${calculateDays()} Days`}</p>
          <p><strong>Reason:</strong> ${form.reason}</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Apply Leave",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setLoading(true);
      try {
        await AxiosAuthInstance.post("hr/leave/apply", {
          ...form,
          days_count: calculateDays(),
        });
        showSnackbar("Leave applied successfully!", "success");
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Your leave application has been submitted.",
          timer: 2000,
          showConfirmButton: false,
        });
        setForm({ leave_type_id: "", start_date: "", end_date: "", duration_type: "full_day", reason: "" });
        setErrors({});
        fetchLeaveData();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to apply leave";
        showSnackbar(errorMsg, "error");
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (field === "duration_type" && value === "half_day" && form.start_date) {
      setForm(prev => ({ ...prev, duration_type: value, end_date: prev.start_date }));
    }
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const getBalanceStatus = (balance) => {
    if (!balance || balance.allocated === 0) return "good";
    const percentage = (balance.remaining / balance.allocated) * 100;
    if (percentage > 50) return "good";
    if (percentage > 20) return "warning";
    return "danger";
  };

  const calculateDays = () => {
    if (!form.start_date || !form.end_date) return 0;
    if (form.duration_type === "half_day") return 0.5;
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : 0;
  };

  const selectedBalance = form.leave_type_id ? balances[form.leave_type_id] : null;

  if (loadingData) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Apply Leave" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <FormCard>
          <Box sx={{ p: 4 }}>
            <Skeleton variant="text" width="40%" height={40} />
            <Skeleton variant="rectangular" height={56} sx={{ mb: 3, borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={56} sx={{ mb: 3, borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          </Box>
        </FormCard>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Apply Leave" />

      <GradientPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: { xs: "center", sm: "space-between" }, alignItems: "center", flexWrap: "wrap", gap: 2, textAlign: { xs: "center", sm: "left" } }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", sm: "flex-start" }, textAlign: { xs: "center", sm: "left" } }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaClipboardList style={{ marginRight: 10, verticalAlign: "middle" }} />
              Apply for Leave
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Submit your leave request with ease
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchLeaveData} sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.3)" } }}>
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <FormCard>
            <Box sx={{ p: 4 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Leave Application Form
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Fill in the details below to submit your leave request
              </Typography>

              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Leave Type <span style={{ color: "#f44336" }}>*</span>
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    value={form.leave_type_id}
                    onChange={(e) => handleInputChange("leave_type_id", e.target.value)}
                    error={!!errors.leave_type_id}
                    helperText={errors.leave_type_id}
                    placeholder="Select leave type"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  >
                    {types.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Duration Type <span style={{ color: "#f44336" }}>*</span>
                  </Typography>
                  <Stack direction="row" spacing={3}>
                    <FormControlLabel
                      control={<Radio checked={form.duration_type === "full_day"} onChange={() => handleInputChange("duration_type", "full_day")} value="full_day" sx={{ color: "#667eea", '&.Mui-checked': { color: "#667eea" } }} />}
                      label="Full Day"
                    />
                    <FormControlLabel
                      control={<Radio checked={form.duration_type === "half_day"} onChange={() => handleInputChange("duration_type", "half_day")} value="half_day" sx={{ color: "#667eea", '&.Mui-checked': { color: "#667eea" } }} />}
                      label="Half Day (0.5 Day)"
                    />
                  </Stack>
                </Box>

                {selectedBalance && (
                  <BalanceCard status={getBalanceStatus(selectedBalance)} elevation={0}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                      <FaInfoCircle style={{ fontSize: 24, color: "#1976d2" }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        Your Leave Balance
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Allocated</Typography>
                        <Typography variant="h5" fontWeight={700} color="#1565c0">{selectedBalance.allocated}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Used</Typography>
                        <Typography variant="h5" fontWeight={700} color="#f44336">{selectedBalance.used}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Remaining</Typography>
                        <Typography variant="h5" fontWeight={700} color="#4caf50">{selectedBalance.remaining}</Typography>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedBalance.used / (selectedBalance.allocated || 1)) * 100}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "rgba(0,0,0,0.1)",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: getBalanceStatus(selectedBalance) === "good" ? "#4caf50" : getBalanceStatus(selectedBalance) === "warning" ? "#ff9800" : "#f44336",
                          },
                        }}
                      />
                    </Box>
                    {selectedBalance.carry_forward > 0 && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <small><strong>Carry Forward:</strong> {selectedBalance.carry_forward} days from previous year</small>
                      </Alert>
                    )}
                  </BalanceCard>
                )}

                {myLeaves.filter(l => l.status === 'rejected').length > 0 && (
                  <Card sx={{ mt: 2, borderRadius: 2, border: '1px solid #ffcdd2', bgcolor: '#ffebee' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} color="#c62828" gutterBottom>
                        <FaTimesCircle style={{ marginRight: 8 }} /> Rejected Leave Requests
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Stack spacing={2}>
                        {myLeaves.filter(l => l.status === 'rejected').map((leave) => (
                          <Box key={leave.id} sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {leave.leave_type?.name || 'Leave'}
                              </Typography>
                              <Chip label="Rejected" size="small" sx={{ bgcolor: '#ffcdd2', color: '#c62828' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {formatDate(leave.start_date)} - {formatDate(leave.end_date)} ({leave.days_count} days)
                            </Typography>
                            {leave.rejected_reason && (
                              <Alert severity="error" sx={{ mt: 1 }}>
                                <strong>Reason:</strong> {leave.rejected_reason}
                              </Alert>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Date Range <span style={{ color: "#f44336" }}>*</span>
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="date"
                        fullWidth
                        value={form.start_date}
                        onChange={(e) => handleInputChange("start_date", e.target.value)}
                        error={!!errors.start_date}
                        helperText={errors.start_date}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Date().toISOString().split('T')[0], style: { height: '16px', padding: '14px' } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: '56px' } }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Start Date</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="date"
                        fullWidth
                        value={form.end_date}
                        onChange={(e) => handleInputChange("end_date", e.target.value)}
                        error={!!errors.end_date}
                        helperText={errors.end_date}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: form.start_date || new Date().toISOString().split('T')[0], style: { height: '16px', padding: '14px' } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: '56px' } }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>End Date</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {form.start_date && form.end_date && calculateDays() > 0 && (
                  <Alert severity="info" icon={<FaCalendar />}>
                    <strong>Duration:</strong> {calculateDays()} day{calculateDays() > 1 ? "s" : ""}
                    {form.duration_type === "half_day" && " (Half Day)"}
                  </Alert>
                )}

                <Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Reason <span style={{ color: "#f44336" }}>*</span>
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    value={form.reason}
                    onChange={(e) => handleInputChange("reason", e.target.value)}
                    error={!!errors.reason}
                    helperText={errors.reason || `${form.reason.length}/10 characters minimum`}
                    placeholder="Please provide a detailed reason for your leave..."
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>

                <Divider />

                <SubmitButton
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  onClick={submit}
                  startIcon={loading ? null : <FaPaperPlane />}
                >
                  {loading ? "Submitting..." : "Submit Leave Application"}
                </SubmitButton>

                <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center", display: "block" }}>
                  <FaInfoCircle style={{ verticalAlign: "middle", marginRight: 4 }} />
                  Your leave request will be sent to your manager for approval
                </Typography>
              </Stack>
            </Box>
          </FormCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: "0 8px 30px rgba(0,0,0,0.1)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                <FaInfoCircle style={{ marginRight: 8, verticalAlign: "middle" }} /> Leave Policy
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="primary">Application Process</Typography>
                  <Typography variant="body2" color="text.secondary">Submit your leave request at least 3 days in advance for planned leaves.</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="primary">Approval Timeline</Typography>
                  <Typography variant="body2" color="text.secondary">Your manager will review and approve/reject within 24-48 hours.</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="primary">Important Notes</Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Ensure you have sufficient leave balance<br />
                    • Provide detailed reason for faster approval<br />
                    • Emergency leaves may require additional documentation
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {Object.keys(balances).length > 0 && (
            <Card sx={{ mt: 3, borderRadius: 3, boxShadow: "0 8px 30px rgba(0,0,0,0.1)" }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  <FaClipboardList style={{ marginRight: 8, verticalAlign: "middle" }} /> Your Leave Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {Object.entries(balances).map(([typeId, balance]) => {
                    const typeName = balance.name || "Unknown";
                    return (
                      <Box key={typeId}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{typeName}</Typography>
                          <Chip
                            label={`${balance.remaining}/${balance.allocated}`}
                            size="small"
                            sx={{
                              bgcolor: getBalanceStatus(balance) === "good" ? "#e8f5e9" : getBalanceStatus(balance) === "warning" ? "#fff3e0" : "#ffebee",
                              color: getBalanceStatus(balance) === "good" ? "#2e7d32" : getBalanceStatus(balance) === "warning" ? "#ef6c00" : "#c62828",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(balance.used / (balance.allocated || 1)) * 100}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: "rgba(0,0,0,0.1)",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: getBalanceStatus(balance) === "good" ? "#4caf50" : getBalanceStatus(balance) === "warning" ? "#ff9800" : "#f44336",
                            },
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveApply;